import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { contact } from '@/content/about';
import { site } from '@/content/site';
import { submitContactRequest, type ContactRequest } from '@/lib/contact';

type Errors = Partial<Record<keyof ContactRequest, string>>;
type Status = 'idle' | 'sending' | 'sent' | 'failed';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Les messages sont explicites et textuels : la gravité passe par les mots. */
function validate(request: ContactRequest): Errors {
  const errors: Errors = {};

  if (request.name.trim().length < 2) errors.name = 'Indiquez votre nom.';
  if (!EMAIL.test(request.email.trim())) errors.email = 'Cette adresse e-mail semble incomplète.';
  if (request.message.trim().length < 10) errors.message = 'Écrivez votre message en une phrase au moins.';

  return errors;
}

export function ContactForm() {
  const [values, setValues] = useState<ContactRequest>({
    name: '',
    email: '',
    subject: contact.subjects[0],
    message: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');

  const update = (field: keyof ContactRequest) => (value: string) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setStatus('sending');
    submitContactRequest({
      name: values.name.trim(),
      email: values.email.trim(),
      subject: values.subject,
      message: values.message.trim(),
    })
      .then(() => setStatus('sent'))
      .catch(() => setStatus('failed'));
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="grid gap-4">
      <TextField
        id="contact-nom"
        name="name"
        label="Nom"
        autoComplete="name"
        placeholder="Votre nom"
        value={values.name}
        error={errors.name}
        onChange={(event) => update('name')(event.target.value)}
      />
      <TextField
        id="contact-email"
        name="email"
        type="email"
        label="E-mail"
        autoComplete="email"
        placeholder="vous@exemple.ch"
        value={values.email}
        error={errors.email}
        onChange={(event) => update('email')(event.target.value)}
      />
      <SelectField
        id="contact-sujet"
        name="subject"
        label="Sujet"
        value={values.subject}
        onChange={(event) => update('subject')(event.target.value)}
      >
        {contact.subjects.map((subject) => (
          <option key={subject}>{subject}</option>
        ))}
      </SelectField>
      <TextAreaField
        id="contact-message"
        name="message"
        label="Message"
        placeholder="Votre message"
        rows={5}
        value={values.message}
        error={errors.message}
        onChange={(event) => update('message')(event.target.value)}
      />

      <div role="status" aria-live="polite" className="text-body-xs font-medium text-ciel empty:hidden">
        {status === 'sent' && 'Message envoyé. Je réponds sous quelques jours.'}
        {status === 'failed' && `L'envoi n'a pas abouti. Écrivez directement à ${site.email}.`}
      </div>

      <Button type="submit" variant="ciel" disabled={status === 'sending'} className="justify-self-start">
        {status === 'sending' ? 'Envoi en cours…' : status === 'sent' ? 'Message envoyé' : 'Envoyer'}
      </Button>
    </form>
  );
}
